import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, setSuspended } from '../slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithLogout = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // Get the URL from the request
  const url = typeof args === 'string' ? args : args.url;

  // Don't logout for auth endpoints (login, register, OTP verification, etc.)
  const isAuthEndpoint = url && url.startsWith('/auth/');

  // If we get a 401 Unauthorized response and it's not an auth endpoint, logout the user
  if (result.error && result.error.status === 401 && !isAuthEndpoint) {
    api.dispatch(logout());
    // Redirect to login page
    window.location.href = '/login';
  }

  // MER-02: 403 ACCOUNT_SUSPENDED — block the dashboard behind the popup, no logout.
  if (result.error && result.error.status === 403 && result.error.data?.code === 'ACCOUNT_SUSPENDED') {
    api.dispatch(setSuspended({ reason: result.error.data.reason || null }));
  }

  return result;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithLogout,
  endpoints: (builder) => ({
    register: builder.mutation({
      query: (credentials) => ({
        url: '/auth/register',
        method: 'POST',
        body: credentials,
      }),
    }),

    verifyRegistration: builder.mutation({
      query: (data) => ({
        url: '/auth/verify-registration',
        method: 'POST',
        body: data,
      }),
    }),

    login: builder.mutation({
      query: (credentials) => {
        // "Trust this device": present the stored trust token so the server can
        // skip the email-OTP step for this device.
        const deviceToken = localStorage.getItem('maxelpay_device_token');
        return {
          url: '/auth/login',
          method: 'POST',
          body: credentials,
          headers: deviceToken ? { 'x-device-token': deviceToken } : {},
        };
      },
    }),

    verifyLogin: builder.mutation({
      query: (data) => ({
        url: '/auth/verify-login',
        method: 'POST',
        body: data,
      }),
    }),

    verifyLogin2FA: builder.mutation({
      query: (data) => ({
        url: '/auth/verify-login-2fa',
        method: 'POST',
        body: data,
      }),
    }),

    forgotPassword: builder.mutation({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),

    resetPassword: builder.mutation({
      query: (data) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: data,
      }),
    }),

    resendOtp: builder.mutation({
      query: (data) => ({
        url: '/auth/resend-otp',
        method: 'POST',
        body: data,
      }),
    }),

    googleAuth: builder.mutation({
      query: (data) => {
        // Same device-trust header as /auth/login. The backend's /auth/google
        // reads x-device-token and has a full trusted-device branch (skip the
        // email OTP, and hand a 2FA account to its TOTP step), but without this
        // header that branch could never be reached: Google sign-in always fell
        // through to the OTP path even on a device the user had chosen to trust,
        // and GoogleSignInButton's `data.token` case was dead code.
        const deviceToken = localStorage.getItem('maxelpay_device_token');
        return {
          url: '/auth/google',
          method: 'POST',
          body: data,
          headers: deviceToken ? { 'x-device-token': deviceToken } : {},
        };
      },
    }),

    getMe: builder.query({
      query: () => '/auth/me',
    }),
  }),
});

export const {
  useRegisterMutation,
  useVerifyRegistrationMutation,
  useLoginMutation,
  useVerifyLoginMutation,
  useVerifyLogin2FAMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useResendOtpMutation,
  useGoogleAuthMutation,
  useGetMeQuery,
} = authApi;
